import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import { useTranslation } from "react-i18next";
import s1 from "../assets/b1.jpg";
import s2 from "../assets/portfolio/IMG_1673.JPG";
import s3 from "../assets/portfolio/C59A1007b.jpg";
import s4 from "../assets/portfolio/e2ccd50a-3817-4ab1-a70a-f0419e19aeea.jpg";
import s5 from "../assets/portfolio/bbf32b17-00ad-4778-8d9c-66084a16b8fe.jpg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');
  *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
  :root{
    --cream:#F7F3EE;--cream-dark:#EDE7DC;--warm-tan:#C9AA8C;
    --terracotta:#B87355;--mocha:#7A5C47;--ink:#2E1F14;
    --ink-soft:#5C4033;--gold:#C49A6C;
    --font-serif:'Cormorant Garamond',Georgia,serif;
    --font-sans:'Jost',sans-serif;
  }
  html{scroll-behavior:smooth;}
  body{background:var(--cream);color:var(--ink);font-family:var(--font-sans);
    font-weight:300;-webkit-font-smoothing:antialiased;overflow-x:hidden;}
`;

// Static: images + accent. All text via t()
const SERVICE_META = [
  {
    id: 0,
    img: s1,
    accentRaw: "#B87355",
    bg: "#F7F3EE",
    dark: false,
    tKey: "s1",
  },
  {
    id: 1,
    img: s2,
    accentRaw: "#C9AA8C",
    bg: "#2E1F14",
    dark: true,
    tKey: "s2",
  },
  {
    id: 2,
    img: s3,
    accentRaw: "#C49A6C",
    bg: "#EDE7DC",
    dark: false,
    tKey: "s3",
  },
  {
    id: 3,
    img: s4,
    accentRaw: "#B87355",
    bg: "#1E0F0A",
    dark: true,
    tKey: "s4",
  },
  {
    id: 4,
    img: s5,
    accentRaw: "#C9AA8C",
    bg: "#f7f3ee",
    dark: false,
    tKey: "s5",
  },
];
const TOTAL = SERVICE_META.length;

const marqueeAnim = keyframes`
  from{transform:translateX(0);}to{transform:translateX(-50%);}
`;

// ── Styled ─────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: var(--cream);
`;

const MarqueeTrack = styled.div`
  overflow: hidden;
  background: var(--ink);
  padding: 1.1rem 0;
  border-top: 1px solid rgba(247, 243, 238, 0.06);
  border-bottom: 1px solid rgba(247, 243, 238, 0.06);
`;
const MarqueeInner = styled.div`
  display: flex;
  width: max-content;
  animation: ${marqueeAnim} 28s linear infinite;
`;
const MarqueeItem = styled.span`
  font-family: var(--font-serif);
  font-size: 0.85rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(247, 243, 238, 0.25);
  padding: 0 2.5rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 2.5rem;
  &::after {
    content: "✦";
    font-style: normal;
    font-size: 0.5rem;
    color: var(--terracotta);
  }
`;

const Hero = styled.div`
  padding: 14rem 5vw 6rem;
  position: relative;
  overflow: visible;
`;
const HeroEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.6rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 32px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;
const HWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.1rem;
`;
const HHead = styled(motion.h1)`
  font-family: var(--font-serif);
  font-size: clamp(4rem, 9vw, 10rem);
  font-weight: 300;
  line-height: 0.85;
  color: var(--ink);
  letter-spacing: -0.03em;
`;
const HHeadItalic = styled(HHead)`
  font-style: italic;
  color: var(--terracotta);
`;
const HeroWatermark = styled(motion.span)`
  position: absolute;
  right: -2vw;
  bottom: 2rem;
  font-family: var(--font-serif);
  font-size: clamp(14rem, 22vw, 28rem);
  font-weight: 300;
  font-style: italic;
  color: transparent;
  -webkit-text-stroke: 1px rgba(122, 92, 71, 0.06);
  line-height: 1;
  pointer-events: none;
  user-select: none;
`;
const HeroMeta = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 3rem;
  margin-top: 3rem;
  flex-wrap: wrap;
`;
const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;
const MetaLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 0.55rem;
  font-weight: 300;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--warm-tan);
`;
const MetaValue = styled.span`
  font-family: var(--font-serif);
  font-size: 1.1rem;
  font-weight: 300;
  font-style: italic;
  color: var(--ink);
`;

const StackTrack = styled.div`
  position: relative;
  height: ${TOTAL * 100}vh;
`;
const StackSticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  width: 100%;
  overflow: visible;
`;

const Card = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: ${({ $bg }) => $bg};
  will-change: transform;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    grid-template-rows: 45vh 1fr;
  }
`;
const CardImgSide = styled.div`
  position: relative;
  overflow: visible;
`;
const CardImg = styled(motion.div)`
  position: absolute;
  inset: 20%;
  background-image: url(${({ $img }) => $img});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(3rem, 6vw, 8rem) clamp(2.5rem, 5vw, 6rem);
  position: relative;
  overflow: visible;
`;
const GhostNum = styled.div`
  position: absolute;
  bottom: -2rem;
  right: -1rem;
  font-family: var(--font-serif);
  font-size: clamp(10rem, 18vw, 22rem);
  font-weight: 300;
  font-style: italic;
  color: transparent;
  -webkit-text-stroke: 1px
    ${({ $dark }) => ($dark ? "rgba(247,243,238,0.04)" : "rgba(46,31,20,0.05)")};
  line-height: 0.85;
  pointer-events: none;
  user-select: none;
  letter-spacing: -0.04em;
`;
const CardNum = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.58rem;
  font-weight: 300;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${({ $dark }) =>
    $dark ? "rgba(247,243,238,0.35)" : "var(--warm-tan)"};
  margin-bottom: 1.8rem;
  display: block;
`;
const TWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.1rem;
`;
const CTitle = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2.8rem, 4.5vw, 5rem);
  font-weight: 300;
  line-height: 0.88;
  letter-spacing: -0.02em;
  color: ${({ $dark }) => ($dark ? "var(--cream)" : "var(--ink)")};
`;
const CTitleItalic = styled(CTitle)`
  font-style: italic;
  color: ${({ $dark }) => ($dark ? "var(--warm-tan)" : "var(--terracotta)")};
`;
const CDivider = styled(motion.div)`
  width: 36px;
  height: 1px;
  background: ${({ $dark }) =>
    $dark ? "rgba(247,243,238,0.2)" : "rgba(122,92,71,0.3)"};
  margin: 2rem 0;
`;
const CDesc = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.78rem, 1vw, 0.9rem);
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.9;
  color: ${({ $dark }) =>
    $dark ? "rgba(247,243,238,0.5)" : "var(--ink-soft)"};
  max-width: 38ch;
  margin-bottom: 2rem;
`;
const IList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-bottom: 2.5rem;
`;
const IItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 300;
  letter-spacing: 0.1em;
  color: ${({ $dark }) =>
    $dark ? "rgba(247,243,238,0.38)" : "var(--ink-soft)"};
`;
const IDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ $a }) => $a};
  flex-shrink: 0;
`;
const CCta = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  font-family: var(--font-sans);
  font-size: 0.62rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${({ $dark }) => ($dark ? "var(--warm-tan)" : "var(--mocha)")};
  transition: color 0.3s;
  svg {
    transition: transform 0.3s;
  }
  &:hover {
    color: var(--terracotta);
  }
  &:hover svg {
    transform: translateX(5px);
  }
`;

const PDots = styled(motion.div)`
  position: fixed;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 150;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const PDot = styled(motion.div)`
  width: 5px;
  border-radius: 3px;
`;

const CtaBand = styled.div`
  background: var(--cream-dark);
  padding: 8rem 5vw;
  display: none;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2.5rem;
`;
const BRule = styled(motion.div)`
  width: 60px;
  height: 1px;
  background: linear-gradient(90deg, var(--terracotta), var(--gold));
  opacity: 0.5;
`;
const BHead = styled(motion.p)`
  font-family: var(--font-serif);
  font-size: clamp(1.6rem, 3vw, 2.8rem);
  font-weight: 300;
  font-style: italic;
  color: var(--ink);
  line-height: 1.35;
`;
const BSub = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.78rem;
  font-weight: 200;
  letter-spacing: 0.07em;
  line-height: 1.8;
  color: var(--ink-soft);
  max-width: 36ch;
`;
const BBtns = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;
const BBtn = styled(motion.a)`
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 1.1rem 2.8rem;
  background: ${({ $p }) => ($p ? "var(--ink)" : "transparent")};
  color: ${({ $p }) => ($p ? "var(--cream)" : "var(--ink)")};
  border: 1px solid ${({ $p }) => ($p ? "var(--ink)" : "rgba(46,31,20,0.2)")};
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $p }) =>
      $p ? "var(--terracotta)" : "rgba(46,31,20,0.05)"};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  span {
    position: relative;
    z-index: 1;
  }
`;

// ── Stack card ──────────────────────────────────────────────────────────
function StackCard({ service, index, scrollYProgress }) {
  const { t } = useTranslation();
  const isFirst = index === 0;
  const bandStart = index / TOTAL;
  const bandEnd = (index + 1) / TOTAL;
  const num = String(index + 1).padStart(2, "0");
  const k = `services_page.${service.tKey}`;

  const rawY = useTransform(
    scrollYProgress,
    [isFirst ? 0 : bandStart - 0.02, bandStart + 0.08],
    [isFirst ? "0%" : "100%", "0%"],
  );
  const y = useSpring(rawY, { stiffness: 80, damping: 22, mass: 0.8 });
  const rawScale = useTransform(
    scrollYProgress,
    [bandEnd - 0.05, bandEnd],
    [1, 0.96],
  );
  const scale = useSpring(rawScale, { stiffness: 80, damping: 22 });
  const rawOpacity = useTransform(
    scrollYProgress,
    [bandEnd - 0.02, bandEnd + 0.04],
    [1, 0.7],
  );
  const rawImgY = useTransform(
    scrollYProgress,
    [bandStart, bandEnd],
    ["-6%", "6%"],
  );
  const imgY = useSpring(rawImgY, { stiffness: 50, damping: 18 });
  const cOpacity = useTransform(
    scrollYProgress,
    [bandStart, bandStart + 0.08],
    [isFirst ? 1 : 0, 1],
  );
  const cY = useTransform(
    scrollYProgress,
    [bandStart, bandStart + 0.1],
    [isFirst ? "0px" : "30px", "0px"],
  );

  return (
    <Card
      $bg={service.bg}
      style={{ y, scale, opacity: rawOpacity, zIndex: index + 1 }}
    >
      <CardImgSide>
        <CardImg $img={service.img} style={{ y: imgY }} />
      </CardImgSide>
      <CardContent>
        <GhostNum $dark={service.dark}>{num}</GhostNum>
        <motion.div style={{ opacity: cOpacity, y: cY }}>
          <CardNum $dark={service.dark}>
            {num} — {t(`${k}_italic`)}
          </CardNum>
          <TWrap>
            <CTitle $dark={service.dark}>{t(`${k}_title`)}</CTitle>
          </TWrap>
          <TWrap>
            <CTitleItalic $dark={service.dark} as="h2">
              {t(`${k}_italic`)}
            </CTitleItalic>
          </TWrap>
          <CDivider
            $dark={service.dark}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{ transformOrigin: "left" }}
          />
          <CDesc $dark={service.dark}>{t(`${k}_desc`)}</CDesc>
          <IList>
            {[1, 2, 3, 4].map((n, i) => (
              <IItem
                key={n}
                $dark={service.dark}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false }}
                transition={{ delay: i * 0.07, duration: 0.5 }}
              >
                <IDot $a={service.accentRaw} />
                {t(`${k}_i${n}`)}
              </IItem>
            ))}
          </IList>
          <CCta
            $dark={service.dark}
            href="https://wa.me/4915000000000"
            target="_blank"
          >
            {t("services_page.cta_contact")}
            <svg width="16" height="8" viewBox="0 0 16 8" fill="none">
              <path
                d="M1 4h14M11 1l4 3-4 3"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CCta>
        </motion.div>
      </CardContent>
    </Card>
  );
}

function SingleDot({ index, scrollYProgress }) {
  const height = useTransform(
    scrollYProgress,
    [
      (index - 0.1) / TOTAL,
      index / TOTAL,
      (index + 1) / TOTAL,
      (index + 1.1) / TOTAL,
    ],
    ["5px", "22px", "22px", "5px"],
  );
  const background = useTransform(
    scrollYProgress,
    [
      (index - 0.1) / TOTAL,
      index / TOTAL,
      (index + 1) / TOTAL,
      (index + 1.1) / TOTAL,
    ],
    [
      "rgba(46,31,20,0.15)",
      "var(--terracotta)",
      "var(--terracotta)",
      "rgba(46,31,20,0.15)",
    ],
  );
  return <PDot style={{ height, background }} />;
}

function Cta() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  return (
    <CtaBand ref={ref}>
      <BRule
        initial={{ scaleX: 0 }}
        animate={inView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.7 }}
      />
      <BHead
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.15, duration: 0.8 }}
      >
        {t("services_page.cta_heading")}
      </BHead>
      <BSub
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.25, duration: 0.8 }}
      >
        {t("services_page.cta_sub")}
      </BSub>
      <BBtns
        initial={{ opacity: 0, y: 10 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.38, duration: 0.7 }}
      >
        <BBtn
          $p
          href="https://wa.me/4915000000000"
          target="_blank"
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
        >
          <span>{t("services_page.cta_wa")}</span>
        </BBtn>
        <BBtn
          href="https://instagram.com/lenamueller.beauty"
          target="_blank"
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
        >
          <span>{t("services_page.cta_ig")}</span>
        </BBtn>
      </BBtns>
    </CtaBand>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────
export default function ServicesPage() {
  const { t } = useTranslation();
  const trackRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Services - Laureta Martini";
  }, []);

  const marqueeItems = [
    t("services_page.s1_title"),
    t("services_page.s2_title"),
    t("services_page.s3_title"),
    t("services_page.s4_title"),
    t("services_page.s5_title"),
    "München & beyond",
  ];

  return (
    <>
      <GlobalStyle />
      <Page>
        <Hero>
          <HeroWatermark
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 1.2 }}
          >
            05
          </HeroWatermark>
          <HeroEyebrow>
            <EyebrowDash
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              style={{ transformOrigin: "left" }}
            />
            <Eyebrow
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
            >
              {t("services_page.eyebrow")}
            </Eyebrow>
          </HeroEyebrow>
          <HWrap>
            <HHead
              initial={{ y: "105%" }}
              animate={{ y: "0%" }}
              transition={{
                duration: 0.9,
                delay: 0.2,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {t("services_page.heading1")}
            </HHead>
          </HWrap>
          <HWrap>
            <HHeadItalic
              as={motion.h1}
              initial={{ y: "105%" }}
              animate={{ y: "0%" }}
              transition={{
                duration: 0.9,
                delay: 0.32,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {t("services_page.heading2")}
            </HHeadItalic>
          </HWrap>
          <HeroMeta
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {[
              {
                l: t("services_page.meta_services"),
                v: t("services_page.meta_services_val"),
              },
              {
                l: t("services_page.meta_location"),
                v: t("services_page.meta_location_val"),
              },
              {
                l: t("services_page.meta_pricing"),
                v: t("services_page.meta_pricing_val"),
              },
            ].map((m) => (
              <MetaItem key={m.l}>
                <MetaLabel>{m.l}</MetaLabel>
                <MetaValue>{m.v}</MetaValue>
              </MetaItem>
            ))}
          </HeroMeta>
        </Hero>

        <MarqueeTrack>
          <MarqueeInner>
            {[
              ...marqueeItems,
              ...marqueeItems,
              ...marqueeItems,
              ...marqueeItems,
            ].map((item, i) => (
              <MarqueeItem key={i}>{item}</MarqueeItem>
            ))}
          </MarqueeInner>
        </MarqueeTrack>

        <StackTrack ref={trackRef}>
          <StackSticky>
            {SERVICE_META.map((s, i) => (
              <StackCard
                key={s.id}
                service={s}
                index={i}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </StackSticky>
        </StackTrack>

        <PDots
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {SERVICE_META.map((s, i) => (
            <SingleDot key={s.id} index={i} scrollYProgress={scrollYProgress} />
          ))}
        </PDots>

        <Cta />
      </Page>
    </>
  );
}
